import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuestionAttemptEntityQuery, QuestionAttemptEntityService, QuestionAttemptHttpModel } from '@examdojo/question';
import { RealtimeChangesListenEvent, RealtimeService } from '@examdojo/supabase';
import { filter, map, Observable, tap, withLatestFrom } from 'rxjs';
import { ExamdojoPlatformService } from '../shared/platform.service';

export abstract class QuestionAttemptRealtimeBaseService {
  constructor() {
    this.setupListeners().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  protected readonly destroyRef = inject(DestroyRef);
  protected readonly realtimeService = inject(RealtimeService);
  protected readonly questionAttemptQuery = inject(QuestionAttemptEntityQuery);
  protected readonly questionAttemptService = inject(QuestionAttemptEntityService);
  protected readonly platformService = inject(ExamdojoPlatformService);

  protected abstract setupListeners(): Observable<unknown>;

  protected handleGradedStatus(record: QuestionAttemptHttpModel) {
    return this.questionAttemptService.synchronizePostGradingState(record);
  }

  protected handleSubmittedStatus(record: QuestionAttemptHttpModel) {
    this.questionAttemptService.setLocalAttempt(record);
  }

  protected handleFailedStatus(record: QuestionAttemptHttpModel) {
    this.questionAttemptService.setLocalAttempt(record);
  }

  protected listenToAttemptStatusChanges(attemptId: string) {
    return this.realtimeService
      .listenBroadcastRealtime<QuestionAttemptHttpModel>(
        [{ event: RealtimeChangesListenEvent.UPDATE }],
        `question_attempt_status:${attemptId}`,
      )
      .pipe(
        withLatestFrom(this.platformService.activeStatusState$),
        filter(([_, isActive]) => isActive),
        map(([event]) => event),
        withLatestFrom(this.questionAttemptQuery.selectLoading()),
        filter(([_, loading]) => !loading),
        tap(([payload]) => {
          if (
            payload.event === RealtimeChangesListenEvent.UPDATE &&
            payload?.payload?.record?.id &&
            payload.payload.record.status === 'SUBMITTED' &&
            this.questionAttemptQuery.getEntity(attemptId)?.attempt?.status !== 'SUBMITTED'
          ) {
            return this.handleSubmittedStatus(payload.payload.record);
          }

          if (
            payload.event === RealtimeChangesListenEvent.UPDATE &&
            payload?.payload?.record?.id &&
            payload.payload.record.status === 'FAILED' &&
            this.questionAttemptQuery.getEntity(attemptId)?.attempt?.status !== 'FAILED'
          ) {
            return this.handleFailedStatus(payload.payload.record);
          }

          if (
            payload.event === RealtimeChangesListenEvent.UPDATE &&
            payload?.payload?.record?.id &&
            payload.payload.record.status === 'GRADED' &&
            this.questionAttemptQuery.getEntity(attemptId)?.attempt?.status !== 'GRADED'
          ) {
            return this.handleGradedStatus(payload.payload.record);
          }
        }),
      );
  }
}
